<template>
    <VSelect
        v-model="val"
        :variant="variant"
        :label="label"
        :items="items"
        :itemProps="getProps"
        :placeholder="placeholder"
        :persistentPlaceholder="persistent"
        :density="density"
        :prefix="prefix"
        :suffix="suffix"
        :messages="messages"
        :disabled="disabled"
        :readonly="readonly"
        :clearable="clearable"
        :loading="loading"
        :multiple="true"
        :errorMessages="errorMessages"
        :noDataText="t({ key: 'shared.no_alternatives' })">
        <template #selection="{ item, index }">
            <div v-if="index === 0" class="truncate-selection">
                {{ item.title }}
            </div>
            <div v-else-if="index === 1">
                +{{ val.length - 1 }} {{ t({ key: 'shared.more' }) }}
            </div>
        </template>
    </VSelect>
</template>

<script lang="ts">
    import { t } from '@shared/helpers/translation';
    import { computed, defineComponent, watch, ref, PropType } from 'vue';
    import { VSelect } from 'vuetify/components';
    import { SelectItem, Variant, Density } from '../../Shared/interfaces/Select.interfaces';
    import { isEqual } from 'lodash';

    export default defineComponent({
        name: 'SelectMultiple',
        methods: { t },
        components: { VSelect },
        emits: ['update:modelValue'],
        props: {
            modelValue: {
                type: Array as PropType<string[]>,
                default: () => [],
            },
            items: { type: Array as PropType<SelectItem[]>, default: () => [] },
            label: String,
            placeholder: String,
            prefix: String,
            suffix: String,
            loading: { type: Boolean as PropType<boolean>, default: false },
            disabled: { type: Boolean as PropType<boolean>, default: false },
            clearable: { type: Boolean as PropType<boolean>, default: false },
            readonly: { type: Boolean as PropType<boolean>, default: false },
            messages: { type: Array as PropType<string[]> },
            errorMessages: {
                type: [Array, String] as PropType<string[] | string>,
            },
            variant: { type: String as PropType<Variant>, default: 'outlined' },
            density: { type: String as PropType<Density>, default: 'default' },
        },
        setup(props, { emit }) {
            const val = ref<string[]>(props.modelValue);
            const persistent = computed(() => Boolean(val.value.length));

            watch(
                () => props.modelValue,
                (newValue, prevValue) => {
                    if (!isEqual(newValue, prevValue)) {
                        val.value = newValue;
                    }
                }
            );

            watch(
                () => val.value,
                (newValue, prevValue) => {
                    if (!isEqual(newValue, prevValue)) {
                        emit('update:modelValue', newValue);
                    }
                }
            );

            const getProps = (item: SelectItem) => {
                return {
                    title: item.label,
                    subtitle: item.subLabel,
                };
            };

            return {
                val,
                persistent,
                getProps,
            };
        },
    });
</script>

<style lang="scss" scoped>
    .truncate-selection {
        white-space: nowrap;
        overflow: hidden;
        text-overflow: ellipsis;
        display: inline-block;
        max-width: 100%;
    }
</style>
